import { updateWorkingOrderState, loadReviewTabState, loadWorkingOrder } from "actions/working-order-state";
import type {
    IReviewTabStateAction,
    LoadWorkingOrderAction,
    UpdateWorkingOrderStateAction,
} from "actions/working-order-state";
import {
    INVENTORY,
    TEMPLATES,
    WORKING_ORDER_STATE,
    BRAND_SHIPPING_CARRIER_COMPLETE,
    WORKING_ORDER,
    PROCESS_ADD_LINE_ITEM_RESULT,
    SHIPPING_RATES,
    COMPANIES,
} from "constants/action-types";
import { transitGridSettings } from "containers/legacy-cart/helpers/common";
import { Observable } from "rxjs";
import App from "lib/app";
import type { IStore } from "reducers";
import _ from "lodash";
import type { Store } from "redux";
import { fetchTemplates } from "actions/templates-actions";
import type { ActionsObservable } from "redux-observable";
import { formatCarriersResponse, shouldFetchCarriers } from "helpers/shipping";
import { fetchAccountCarriers } from "actions/shipping-accounts";
import { fetchShippingRates } from "actions/shipping-rates";
import { checkShippingRatesReady, getShippingKey } from "helpers/shipping-rates";
import { ISendLineItemResultAction } from "types/working-order/line-items";
import { fetchProductsAvailability, fetchWorkingProducts } from "actions/working-order/products";
import { fetchWorkingOrder } from "actions/working-order/order";
import { fetchCompaniesById, type IFetchCompaniesMeta } from "actions/companies";
import type { ICompany } from "components/contacts/contacts-types";
import { resetWorkingCompany, setWorkingCompany } from "actions/working-order/company";
import { fetchWarehouses } from "actions/inventory";
import { loadInventory, clearAvailabilityCache } from "actions/working-order/inventory";
import { MARKETPLACE } from "constants/navigation";

const LOAD_DEBOUNCE_MS = 500;

/** Should be called when we need to send an update of Working Order state */
export const updateWorkingOrderStateEpic = (action$, store: Store<IStore>) =>
    action$.ofType(WORKING_ORDER_STATE.UPDATE).mergeMap(({ payload, meta }: UpdateWorkingOrderStateAction) => {
        const { cartGridView, templates, shippingAccountCarriers, shippingRates } = store.getState();

        App.legacy.sendMessage({
            action: WORKING_ORDER_STATE.UPDATE,
            meta,
            appData: {
                ...(payload.cartGridView && { cartGridView: transitGridSettings(cartGridView, "pack") }),
                ...(payload.replenishments && { replenishments: payload.replenishments }), // replenishments chunk
                ...(payload.exportTemplates && { exportTemplates: _.filter(templates.payload, "active") }),
                ...(payload.warehouses && { warehouses: payload.warehouses }),
                ...(payload.products && { products: payload.products }), // products chunk
                ...(payload.carriers && { carriers: formatCarriersResponse(shippingAccountCarriers) }),
                ...(payload.shippingRates && { shippingRates: shippingRates.payload }),
                ...(payload.availability && { availability: payload.availability }),
            },
        });

        return [];
    });

/** Should be called when we need to notify WO about add line item operation result. */
export const sendAddLineItemResultEpic = (action$) =>
    action$
        .ofType(WORKING_ORDER.LINE_ITEM.SEND_RESULT)
        .mergeMap(({ payload, meta, error }: ISendLineItemResultAction) => {
            App.legacy.sendMessage({
                action: PROCESS_ADD_LINE_ITEM_RESULT,
                meta,
                payload,
                error: (error || _.isEmpty(payload?.lineItems)) && {
                    message: error?.message,
                    options: {
                        unavailableInventory: !_.isEmpty(payload?.rejectedItemsGroupedByCause?.inventory),
                        unavailableWarehouse: !_.isEmpty(payload?.rejectedItemsGroupedByCause?.warehouse),
                    },
                },
            });
            return [];
        });

/**
 * Should be called when we need to open Working Order.
 * Triggers `load` only when Working Order is fetched and ready to fetch dependent data.
 */
export const openWorkingOrderEpic = (action$, store: Store<IStore>) =>
    action$
        .ofType(WORKING_ORDER_STATE.OPEN)
        .filter(() => !store.getState().working.order.isFetching)
        .mergeMap((action) => [
            resetWorkingCompany(),
            clearAvailabilityCache(),
            fetchWorkingOrder({ loadCart: action.payload || {} }),
        ]);
/**
 * Should be called when it is required to close Working Order.
 * TODO: Should cancel all the in-progress fetching for working order.
 */
export const closeWorkingOrderEpic = (action$) =>
    action$.ofType(WORKING_ORDER_STATE.CLOSE).mergeMap((action) => {
        App.legacy.sendMessage({ ...action?.payload, action: "toggle-cart", open: false });
        // return [WORKING_ORDER.PRODUCTS.FETCH.CANCEL, WORKING_ORDER.ORDER.FETCH.CANCEL, ...].map((x) => ({ type: x }));
        return [];
    });

/**
 * Should be called when we need to initially open working order.
 * Should notify Working Order only when all necessary data is fetched.
 */
export const loadWorkingOrderState = (action$, store: Store<IStore>) =>
    action$
        .ofType(WORKING_ORDER_STATE.LOAD)
        .debounce(() => Observable.timer(LOAD_DEBOUNCE_MS))
        // TODO: cancel previous load .takeUntil(action$.ofType(WORKING_ORDER_STATE.CLOSE))
        .mergeMap((action: LoadWorkingOrderAction) => {
            const state = store.getState();
            const { cartGridView, templates, scope, inventory, working, shippingAccountCarriers, app } = state;

            const loadActions = [];

            loadActions.push({ type: WORKING_ORDER_STATE.DATA });

            const brandWarehouses = inventory.warehouses.payload?.[scope.brand._id];
            const selectedCompany = fetchWorkingOrderCompany(store, { loadCart: action.payload || {} });

            const productIds = _.map(working.order.payload?.line_items, "product");
            const productsToLoad = _.difference(productIds, _.keys(working?.products?.payload));
            const productsWithoutAvailability = _.uniq(_.difference(productIds, working?.availability?.cache));

            const requireProducts =
                !_.isEmpty(productsToLoad) && !working?.products?.isFetching && selectedCompany !== undefined;
            const requireAvailability =
                !_.isEmpty(productsWithoutAvailability) &&
                !working?.availability?.isFetching &&
                _.isEmpty(working?.availability?.error);
            const requireCarriers =
                !(shippingAccountCarriers.data || !shouldFetchCarriers(state)) && !shippingAccountCarriers.isFetching;
            const requireWarehouses = !brandWarehouses && !inventory.warehouses.isFetching;
            const requireReplenishments =
                !inventory.replenishments.isFetching && _.isEmpty(productsToLoad) && !working?.products?.isFetching;

            const meta = { loadCart: action.payload || {} };

            if (requireProducts) {
                loadActions.push(
                    fetchWorkingProducts(
                        {
                            ids: productsToLoad,
                            company: selectedCompany?._id,
                            activePricesheet: selectedCompany?.pricing_template,
                        },
                        meta,
                    ),
                );
            }

            if (requireAvailability) {
                loadActions.push(fetchProductsAvailability({ ids: productsWithoutAvailability }, meta));
            }

            if (requireCarriers) {
                loadActions.push(fetchAccountCarriers(meta));
            }

            if (requireWarehouses) {
                loadActions.push(fetchWarehouses(scope.brand._id, {}, meta));
            }

            if (requireReplenishments) {
                loadActions.push(loadInventory(productIds, { ...meta, notifyCart: true }));
            }

            // TODO: All required data is fetched (order, products, company, inventory and replenishments)
            const allFetched =
                !requireProducts &&
                !requireAvailability &&
                !requireCarriers &&
                selectedCompany !== undefined &&
                !requireWarehouses;
            if (allFetched && app.legacyLoaded) {
                App.legacy.sendMessage({
                    action: "toggle-cart",
                    ...action.payload,
                    open: true,
                    clearCompany: working?.availability?.error?.status === 400,
                    appData: {
                        order: working.order.payload,
                        company: selectedCompany,
                        products: _.values(working?.products?.payload),
                        availability: working?.availability?.payload,
                        cartGridView: transitGridSettings(cartGridView, "pack"),
                        ...(templates.payload && { exportTemplates: _.filter(templates.payload, "active") }),
                        carriers: formatCarriersResponse(shippingAccountCarriers),
                        warehouses: brandWarehouses,
                    },
                    marketplace: !!window.sessionStorage.getItem("lastActivePath")?.includes(MARKETPLACE),
                });
            } else if (!app.legacyLoaded) {
                loadActions.push(loadWorkingOrder(action.payload));
            }

            return loadActions;
        });

export const updateReviewTabStateEpic = (action$, store: Store<IStore>) =>
    action$.ofType(WORKING_ORDER_STATE.REVIEW.UPDATE).mergeMap(({ payload, meta }) => {
        App.legacy.sendMessage({
            action: WORKING_ORDER_STATE.REVIEW.UPDATE,
            meta,
            appData: {},
        });

        return [];
    });

export const loadReviewTabEpic = (action$: ActionsObservable<IReviewTabStateAction>, store: Store<IStore>) =>
    action$
        .ofType(WORKING_ORDER_STATE.REVIEW.LOAD)
        .debounce(() => Observable.timer(LOAD_DEBOUNCE_MS))
        .mergeMap((action: IReviewTabStateAction) => {
            const state = store.getState();
            const shippingKey = action.meta.shippingKey || getShippingKey(action.payload, action.meta);

            if (checkShippingRatesReady(state, shippingKey)) {
                App.legacy.sendMessage({
                    action: WORKING_ORDER_STATE.REVIEW.LOAD,
                    meta: action.meta,
                    appData: {
                        shippingRates: state.shippingRates.payload, // TODO: appData is not used anywhere
                    },
                });
                return [];
            }

            if (!state.shippingRates.isFetching) {
                return [fetchShippingRates(action.payload, { ...action.meta, loadReview: true, shippingKey })];
            }

            return [];
        });

export const replenishmentsWorkingOrderEpic = (action$) =>
    action$
        .ofType(INVENTORY.REPLENISHMENTS.FETCH.SUCCESS)
        .filter((action) => action?.meta?.notifyCart)
        .mergeMap(({ payload, meta }) => [updateWorkingOrderState({ replenishments: payload }, meta)]);

export const exportTemplatesWorkingOrderEpic = (action$) =>
    action$
        .ofType(TEMPLATES.FETCH.SUCCESS)
        .filter((action) => action?.meta?.notifyCart)
        .mergeMap(() => [updateWorkingOrderState({ exportTemplates: true })]);

export const workingOrderWarehousesEpic = (action$) =>
    action$
        .ofType(INVENTORY.WAREHOUSES.FETCH.SUCCESS)
        .filter(({ meta }) => meta?.loadCart)
        .mergeMap((action) => [loadWorkingOrder(action.meta.loadCart)]);

export const getCarriersWorkingOrderEpic = (action$) =>
    action$
        .ofType(BRAND_SHIPPING_CARRIER_COMPLETE)
        .filter((action) => action?.meta?.loadCart)
        .mergeMap((action) => [loadWorkingOrder(action.meta.loadCart)]);

// Optional data which not block Working Order opening
export const requestWorkingOrderData = (action$, store: Store<IStore>) =>
    action$.ofType(WORKING_ORDER_STATE.DATA).mergeMap(() => {
        const { scope, templates } = store.getState();
        if (scope.brand.hasPreference("custom_exports") && !templates.isFetching) {
            return templates.payload
                ? [updateWorkingOrderState({ exportTemplates: true })]
                : [{ ...fetchTemplates(), meta: { notifyCart: true } }];
        }
        return [];
    });

export const shippingRatesReviewTabEpic = (action$) =>
    action$
        .ofType(SHIPPING_RATES.FETCH.SUCCESS)
        .filter(({ meta }) => meta?.loadReview)
        .mergeMap(({ meta }) => [loadReviewTabState({}, meta)]);

export const fetchWorkingOrderCompanyEpic = (action$) =>
    action$
        .ofType(COMPANIES.FETCH.SUCCESS)
        .filter(({ meta }) => meta?.loadCart)
        .mergeMap(({ payload: companies, meta }) => [
            setWorkingCompany(companies?.[0] ?? null),
            loadWorkingOrder(meta.loadCart),
        ]);

function fetchWorkingOrderCompany(store: Store<IStore>, meta: IFetchCompaniesMeta): Nullish<ICompany> {
    const companyId = store.getState().working.order.payload?.retailer?.ref;
    if (!companyId) {
        store.dispatch(setWorkingCompany(null));
    }

    const company = store.getState().working.company.payload;
    if (company === undefined && !store.getState().companies.isFetching) {
        store.dispatch(fetchCompaniesById([companyId], meta));
    }
    return company;
}

export const availabilityFetchSuccessEpic = (action$) =>
    action$
        .ofType(WORKING_ORDER.AVAILABILITY.FETCH.SUCCESS)
        .mergeMap(({ payload, meta }) =>
            _.compact([
                meta?.loadCart && loadWorkingOrder(meta.loadCart),
                meta?.notifyCart && updateWorkingOrderState({ availability: payload?.data || {} }, meta),
            ]),
        );
